/**
 * Lemai Analytics Shared Service
 * Consumed by SmartVision, CarPark, SOCDashboard, and any module needing analytics.
 * All data shapes, helpers, and demo-data generators live here.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PeopleCountReading {
  timestamp: string;   // HH:MM
  count: number;
  in: number;
  out: number;
  zone?: string;
}

export interface DirectionalFlow {
  from: string;
  to: string;
  count: number;
  pct: number;
}

export interface HeatCell {
  row: number;
  col: number;
  value: number; // 0-1
}

export interface PersonDetection {
  id: string;
  timestamp: string;
  channel: number;
  camera: string;
  confidence: number;
  gender: 'male' | 'female' | 'unknown';
  ageGroup: 'child' | 'young_adult' | 'adult' | 'senior';
  topColor: string;
  bottomColor: string;
  hasBackpack: boolean;
  hasBag: boolean;
  hasHat: boolean;
  dwellSeconds: number;
  zone?: string;
  isLoitering: boolean;
  snapshotUrl?: string;
}

export interface VehicleDetection {
  id: string;
  timestamp: string;
  channel: number;
  camera: string;
  confidence: number;
  type: 'car' | 'truck' | 'van' | 'motorcycle' | 'bus' | 'bicycle';
  color: string;
  plate?: string;
  direction: 'entering' | 'exiting' | 'unknown';
  parkingBay?: string;
  dwellMinutes?: number;
  snapshotUrl?: string;
}

export interface LoiteringAlert {
  id: string;
  timestamp: string;
  camera: string;
  zone: string;
  dwellSeconds: number;
  personId: string;
  severity: 'low' | 'medium' | 'high';
}

export interface TimelineEvent {
  id: string;
  timestamp: string;
  type: 'person' | 'vehicle' | 'loitering' | 'crowd' | 'motion';
  label: string;
  camera: string;
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  details?: string;
}

export interface AgeGroupBreakdown {
  child: number;
  young_adult: number;
  adult: number;
  senior: number;
}

export interface GenderBreakdown {
  male: number;
  female: number;
  unknown: number;
}

export interface ClothingColorBreakdown {
  color: string;
  hex: string;
  count: number;
  pct: number;
}

export interface CrowdDensityReading {
  timestamp: string;
  density: 'low' | 'medium' | 'high' | 'critical';
  count: number;
  zone: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const ZONES = ['Main Entrance', 'Lobby', 'Car Park Level 1', 'Car Park Level 2', 'Side Gate', 'Council Chambers', 'Civic Square', 'Loading Dock'];

export const AGE_GROUP_LABELS: Record<string, string> = {
  child: 'Child (0–17)',
  young_adult: 'Young Adult (18–35)',
  adult: 'Adult (36–60)',
  senior: 'Senior (60+)',
};

export const VEHICLE_TYPE_LABELS: Record<string, string> = {
  car: 'Passenger Car',
  truck: 'Truck / Heavy Vehicle',
  van: 'Van / Minibus',
  motorcycle: 'Motorcycle',
  bus: 'Bus / Coach',
  bicycle: 'Bicycle',
};

export const CLOTHING_COLORS = [
  { color: 'Black',  hex: '#1a1a1a' },
  { color: 'White',  hex: '#f0f0f0' },
  { color: 'Blue',   hex: '#2563eb' },
  { color: 'Grey',   hex: '#6b7280' },
  { color: 'Red',    hex: '#dc2626' },
  { color: 'Green',  hex: '#16a34a' },
  { color: 'Brown',  hex: '#92400e' },
  { color: 'Yellow', hex: '#ca8a04' },
];

// ─── Demo-data generators ────────────────────────────────────────────────────

function rng(seed: number) {
  let s = seed;
  return () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 4294967296; };
}

export function generateHourlyPeopleCounts(date: string, zone?: string): PeopleCountReading[] {
  const r = rng(date.split('').reduce((a, c) => a + c.charCodeAt(0), 0) + (zone ? zone.length : 0));
  const now = new Date();
  const isToday = date === now.toISOString().slice(0, 10);
  const maxHour = isToday ? now.getHours() : 23;
  const readings: PeopleCountReading[] = [];
  for (let h = 6; h <= maxHour; h++) {
    const base = h >= 8 && h <= 17 ? 40 + r() * 60 : h >= 18 && h <= 21 ? 20 + r() * 30 : 2 + r() * 10;
    const count = Math.round(base);
    const inFlow = Math.round(count * (0.4 + r() * 0.3));
    readings.push({ timestamp: `${String(h).padStart(2,'0')}:00`, count, in: inFlow, out: count - inFlow, zone });
  }
  return readings;
}

export function generateDirectionalFlows(): DirectionalFlow[] {
  const flows: DirectionalFlow[] = [
    { from: 'Main Entrance', to: 'Lobby', count: 342, pct: 48 },
    { from: 'Car Park Level 1', to: 'Main Entrance', count: 178, pct: 25 },
    { from: 'Side Gate', to: 'Lobby', count: 97, pct: 14 },
    { from: 'Main Entrance', to: 'Council Chambers', count: 63, pct: 9 },
    { from: 'Loading Dock', to: 'Lobby', count: 29, pct: 4 },
  ];
  return flows;
}

export function generateHeatmap(rows = 8, cols = 12, seed = 42): HeatCell[] {
  const r = rng(seed);
  const cells: HeatCell[] = [];
  // Create hotspot regions
  const hotspots = [{ row: 2, col: 3 }, { row: 5, col: 9 }, { row: 1, col: 6 }];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      let value = r() * 0.15;
      for (const hs of hotspots) {
        const dist = Math.sqrt(Math.pow(row - hs.row, 2) + Math.pow(col - hs.col, 2));
        if (dist < 3) value += (1 - dist / 3) * 0.85;
      }
      cells.push({ row, col, value: Math.min(1, value) });
    }
  }
  return cells;
}

export function generatePersonDetections(count = 30, cameras: string[] = ['Camera 01', 'Camera 02', 'Camera 03']): PersonDetection[] {
  const r = rng(7777);
  const genders: PersonDetection['gender'][] = ['male', 'female', 'unknown'];
  const ages: PersonDetection['ageGroup'][] = ['child', 'young_adult', 'adult', 'senior'];
  const topColors = CLOTHING_COLORS.map(c => c.color);
  const results: PersonDetection[] = [];
  const now = new Date();

  for (let i = 0; i < count; i++) {
    const minsAgo = Math.round(r() * 480);
    const dt = new Date(now.getTime() - minsAgo * 60000);
    const dwell = Math.round(10 + r() * 300);
    results.push({
      id: `P${String(i + 1).padStart(4, '0')}`,
      timestamp: dt.toTimeString().slice(0, 5),
      channel: Math.floor(r() * cameras.length) + 1,
      camera: cameras[Math.floor(r() * cameras.length)],
      confidence: Math.round(80 + r() * 18),
      gender: genders[Math.floor(r() * genders.length)],
      ageGroup: ages[Math.floor(r() * ages.length)],
      topColor: topColors[Math.floor(r() * topColors.length)],
      bottomColor: topColors[Math.floor(r() * topColors.length)],
      hasBackpack: r() > 0.75,
      hasBag: r() > 0.65,
      hasHat: r() > 0.80,
      dwellSeconds: dwell,
      zone: ZONES[Math.floor(r() * ZONES.length)],
      isLoitering: dwell > 180,
    });
  }
  return results.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

export function generateVehicleDetections(count = 20, cameras: string[] = ['Car Park Cam 01', 'Car Park Cam 02']): VehicleDetection[] {
  const r = rng(3333);
  const types: VehicleDetection['type'][] = ['car', 'car', 'car', 'van', 'truck', 'motorcycle', 'bicycle', 'bus'];
  const colors = ['White', 'Black', 'Silver', 'Blue', 'Red', 'Grey', 'Green'];
  const directions: VehicleDetection['direction'][] = ['entering', 'exiting'];
  const now = new Date();
  const results: VehicleDetection[] = [];

  for (let i = 0; i < count; i++) {
    const minsAgo = Math.round(r() * 480);
    const dt = new Date(now.getTime() - minsAgo * 60000);
    results.push({
      id: `V${String(i + 1).padStart(4, '0')}`,
      timestamp: dt.toTimeString().slice(0, 5),
      channel: Math.floor(r() * cameras.length) + 1,
      camera: cameras[Math.floor(r() * cameras.length)],
      confidence: Math.round(82 + r() * 16),
      type: types[Math.floor(r() * types.length)],
      color: colors[Math.floor(r() * colors.length)],
      plate: r() > 0.5 ? `${String.fromCharCode(65 + Math.floor(r()*26))}${String.fromCharCode(65 + Math.floor(r()*26))}${Math.floor(r()*10)}${Math.floor(r()*10)} ${String.fromCharCode(65 + Math.floor(r()*26))}${String.fromCharCode(65 + Math.floor(r()*26))}` : undefined,
      direction: directions[Math.floor(r() * directions.length)],
      parkingBay: `B${Math.floor(r() * 120) + 1}`,
      dwellMinutes: Math.round(r() * 180),
    });
  }
  return results.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

export function generateLoiteringAlerts(): LoiteringAlert[] {
  return [
    { id: 'L001', timestamp: '14:22', camera: 'Camera 03 — Side Gate', zone: 'Side Gate', dwellSeconds: 820, personId: 'P0014', severity: 'high' },
    { id: 'L002', timestamp: '11:47', camera: 'Camera 01 — Main Entrance', zone: 'Main Entrance', dwellSeconds: 612, personId: 'P0007', severity: 'medium' },
    { id: 'L003', timestamp: '09:15', camera: 'Camera 05 — Car Park L1', zone: 'Car Park Level 1', dwellSeconds: 1240, personId: 'P0022', severity: 'high' },
    { id: 'L004', timestamp: '08:33', camera: 'Camera 02 — Lobby', zone: 'Lobby', dwellSeconds: 290, personId: 'P0003', severity: 'low' },
  ];
}

export function generateAgeBreakdown(detections: PersonDetection[]): AgeGroupBreakdown {
  const result = { child: 0, young_adult: 0, adult: 0, senior: 0 };
  for (const d of detections) result[d.ageGroup]++;
  return result;
}

export function generateGenderBreakdown(detections: PersonDetection[]): GenderBreakdown {
  const result = { male: 0, female: 0, unknown: 0 };
  for (const d of detections) result[d.gender]++;
  return result;
}

export function generateClothingColors(detections: PersonDetection[]): ClothingColorBreakdown[] {
  const counts: Record<string, number> = {};
  for (const d of detections) {
    counts[d.topColor] = (counts[d.topColor] || 0) + 1;
  }
  const total = detections.length || 1;
  return CLOTHING_COLORS
    .map(c => ({ color: c.color, hex: c.hex, count: counts[c.color] || 0, pct: Math.round(((counts[c.color] || 0) / total) * 100) }))
    .sort((a, b) => b.count - a.count)
    .filter(c => c.count > 0);
}

export function generateTimelineEvents(personDetections: PersonDetection[], loitering: LoiteringAlert[]): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  for (const p of personDetections.slice(0, 15)) {
    events.push({ id: `T-${p.id}`, timestamp: p.timestamp, type: 'person', label: `${p.gender === 'female' ? 'Female' : p.gender === 'male' ? 'Male' : 'Person'} detected — ${p.camera}`, camera: p.camera, severity: 'info', details: `${p.ageGroup.replace('_',' ')} • ${p.topColor} top • Dwell: ${p.dwellSeconds}s` });
  }
  for (const l of loitering) {
    events.push({ id: `T-${l.id}`, timestamp: l.timestamp, type: 'loitering', label: `Loitering alert — ${l.zone}`, camera: l.camera, severity: l.severity, details: `Dwell time: ${Math.round(l.dwellSeconds/60)}m ${l.dwellSeconds%60}s` });
  }
  return events.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

// ─── Heatmap renderer (inline SVG) ───────────────────────────────────────────

export function heatmapColor(value: number): string {
  if (value < 0.25) return `rgba(59,130,246,${0.2 + value * 1.2})`;
  if (value < 0.5)  return `rgba(234,179,8,${0.4 + value * 0.8})`;
  if (value < 0.75) return `rgba(249,115,22,${0.6 + value * 0.6})`;
  return `rgba(220,38,38,${0.7 + value * 0.3})`;
}

// ─── Frigate event shape (from connector) ────────────────────────────────────

export interface FrigateEvent {
  id: string;
  camera: string;
  label: string;        // 'person', 'car', 'truck', etc.
  score: number;
  start_time: number;   // unix epoch
  end_time: number | null;
  has_snapshot: boolean;
  has_clip: boolean;
  attributes?: {
    color?: string;
    age?: string;
    gender?: string;
    sub_label?: string;
  };
  regions?: Array<{ x: number; y: number; width: number; height: number }>;
}

export function frigateToPersonDetection(e: FrigateEvent): PersonDetection {
  const dt = new Date(e.start_time * 1000);
  return {
    id: e.id,
    timestamp: dt.toTimeString().slice(0, 5),
    channel: 1,
    camera: e.camera,
    confidence: Math.round(e.score * 100),
    gender: (e.attributes?.gender as any) || 'unknown',
    ageGroup: 'adult',
    topColor: e.attributes?.color || 'unknown',
    bottomColor: 'unknown',
    hasBackpack: false,
    hasBag: false,
    hasHat: false,
    dwellSeconds: e.end_time ? Math.round(e.end_time - e.start_time) : 0,
    isLoitering: e.end_time ? (e.end_time - e.start_time) > 180 : false,
    snapshotUrl: e.has_snapshot ? `/api/frigate/events/${e.id}/snapshot.jpg` : undefined,
  };
}

export function frigateToVehicleDetection(e: FrigateEvent): VehicleDetection {
  const dt = new Date(e.start_time * 1000);
  return {
    id: e.id,
    timestamp: dt.toTimeString().slice(0, 5),
    channel: 1,
    camera: e.camera,
    confidence: Math.round(e.score * 100),
    type: e.label as VehicleDetection['type'],
    color: e.attributes?.color || 'Unknown',
    direction: 'unknown',
    dwellMinutes: e.end_time ? Math.round((e.end_time - e.start_time) / 60) : undefined,
    snapshotUrl: e.has_snapshot ? `/api/frigate/events/${e.id}/snapshot.jpg` : undefined,
  };
}
